import { useState } from "react";
import { AdminNav } from "@/components/admin/AdminNav";
import { getCitySlug } from "@/lib/city";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";

/**
 * Admin correction queue — super_admin / pulseboard_admin only.
 * Route: /admin/corrections
 *
 * Lists facility_correction rows (default: pending). Each row has:
 *   - "Disable listing" → PATCH action=disable → sets facility.active=FALSE
 *   - "Mark fixed"      → PATCH action=fix
 *   - "Dismiss"         → PATCH action=dismiss
 *
 * Auth: uses Keycloak access token via api.get() (same as other authenticated
 * routes). Renders a "not authorised" message if the JWT role isn't admin.
 */

type CorrectionStatus = "pending" | "resolved_fixed" | "resolved_disabled" | "dismissed";

interface Correction {
  id: string;
  facility_slug: string;
  facility_name: string;
  facility_active: boolean;
  reporter_email: string | null;
  reporter_name: string | null;
  reason: string;
  note: string | null;
  status: CorrectionStatus;
  created_at: string;
  resolved_at: string | null;
  admin_note: string | null;
}

const STATUS_LABELS: Record<CorrectionStatus, string> = {
  pending: "Pending",
  resolved_fixed: "Fixed",
  resolved_disabled: "Disabled",
  dismissed: "Dismissed",
};

export default function AdminCorrections() {
  const citySlug = getCitySlug();
  const [filter, setFilter] = useState<CorrectionStatus>("pending");
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-corrections", filter],
    queryFn: () =>
      api.get<{ items: Correction[]; meta: { status: string; total: number } }>(
        `/${citySlug}/v1/admin/corrections?status=${filter}`,
      ),
    retry: false,
  });

  const resolve = useMutation({
    mutationFn: ({
      id,
      action,
      adminNote,
    }: {
      id: string;
      action: "disable" | "fix" | "dismiss";
      adminNote?: string;
    }) =>
      api.patch(`/${citySlug}/v1/admin/corrections/${id}`, { action, adminNote }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-corrections"] });
    },
  });

  return (
    <div className="brand-a" style={{ minHeight: "100dvh", padding: "80px 40px 60px" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <AdminNav />
        <div className="ba-eyebrow" style={{ marginBottom: 16 }}>
          Admin · Corrections
        </div>
        <h1
          className="ba-display"
          style={{ fontSize: 40, marginBottom: 32 }}
        >
          Correction queue
        </h1>

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: "1px solid rgba(15,42,42,0.08)", paddingBottom: 0 }}>
          {(["pending", "resolved_fixed", "resolved_disabled", "dismissed"] as CorrectionStatus[]).map(
            (s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                style={{
                  padding: "8px 16px",
                  border: 0,
                  background: "transparent",
                  borderBottom:
                    filter === s ? "2px solid var(--ba-coral)" : "2px solid transparent",
                  color: filter === s ? "var(--ba-ink)" : "var(--ba-stone-7)",
                  fontFamily: "var(--ba-font-sans)",
                  fontWeight: filter === s ? 500 : 400,
                  fontSize: 14,
                  cursor: "pointer",
                  marginBottom: -1,
                }}
              >
                {STATUS_LABELS[s]}
              </button>
            ),
          )}
        </div>

        {isLoading && (
          <p className="ba-mono ba-mono--dim" style={{ fontSize: 13 }}>
            Loading…
          </p>
        )}

        {isError && (
          <div
            style={{
              padding: 20,
              background: "rgba(192,57,43,0.05)",
              border: "1px solid rgba(192,57,43,0.2)",
              borderRadius: "var(--ba-r)",
              color: "var(--ba-coral)",
              fontSize: 14,
            }}
          >
            Failed to load corrections. Check that you have the{" "}
            <code>super_admin</code> or <code>pulseboard_admin</code> role and are
            signed in.
          </div>
        )}

        {data && data.items.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              border: "1px dashed rgba(15,42,42,0.15)",
              borderRadius: "var(--ba-r-lg)",
            }}
          >
            <p className="ba-mono ba-mono--dim" style={{ fontSize: 13 }}>
              No {filter.replace("_", " ")} corrections.
            </p>
          </div>
        )}

        {data && data.items.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {data.items.map((c) => (
              <CorrectionRow
                key={c.id}
                correction={c}
                onAction={(action, adminNote) =>
                  resolve.mutate({ id: c.id, action, adminNote })
                }
                isPending={resolve.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CorrectionRow({
  correction: c,
  onAction,
  isPending,
}: {
  correction: Correction;
  onAction: (action: "disable" | "fix" | "dismiss", note?: string) => void;
  isPending: boolean;
}) {
  const [note, setNote] = useState("");

  return (
    <article
      style={{
        padding: 20,
        background: "var(--ba-bone-50)",
        border: "1px solid rgba(15,42,42,0.08)",
        borderRadius: "var(--ba-r-lg)",
        display: "grid",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <p
            style={{
              fontFamily: "var(--ba-font-display)",
              fontSize: 20,
              fontWeight: 400,
              margin: "0 0 4px",
              color: "var(--ba-ink)",
            }}
          >
            {c.facility_name}
          </p>
          <p className="ba-mono ba-mono--dim" style={{ fontSize: 11 }}>
            {c.facility_slug}
            {!c.facility_active && (
              <span
                style={{
                  marginLeft: 8,
                  color: "var(--ba-coral)",
                  background: "rgba(242,126,98,0.1)",
                  padding: "2px 6px",
                  borderRadius: 3,
                }}
              >
                DISABLED
              </span>
            )}
          </p>
        </div>
        <p className="ba-mono ba-mono--dim" style={{ fontSize: 11, alignSelf: "flex-start" }}>
          {new Date(c.created_at).toLocaleDateString("en-AU", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </p>
      </div>

      <div>
        <p style={{ fontSize: 15, fontWeight: 500, color: "var(--ba-ink)", margin: "0 0 4px" }}>
          {c.reason}
        </p>
        {c.note && (
          <p style={{ fontSize: 13, color: "var(--ba-stone-7)", margin: 0 }}>{c.note}</p>
        )}
      </div>

      {(c.reporter_name || c.reporter_email) && (
        <p className="ba-mono ba-mono--dim" style={{ fontSize: 11 }}>
          Reporter:{" "}
          {c.reporter_name && <span>{c.reporter_name} </span>}
          {c.reporter_email && (
            <a href={`mailto:${c.reporter_email}`} style={{ color: "var(--ba-teal-600)" }}>
              {c.reporter_email}
            </a>
          )}
        </p>
      )}

      {c.status === "pending" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Admin note (optional)"
            style={{
              flex: "1 1 200px",
              padding: "8px 12px",
              border: "1px solid rgba(15,42,42,0.15)",
              borderRadius: "var(--ba-r)",
              fontFamily: "var(--ba-font-sans)",
              fontSize: 13,
              background: "var(--ba-bone)",
            }}
          />
          <button
            type="button"
            disabled={isPending}
            onClick={() => onAction("disable", note || undefined)}
            style={{
              padding: "8px 14px",
              background: "var(--ba-coral)",
              color: "#fff",
              border: 0,
              borderRadius: "var(--ba-r)",
              fontFamily: "var(--ba-font-sans)",
              fontSize: 13,
              fontWeight: 500,
              cursor: isPending ? "not-allowed" : "pointer",
              opacity: isPending ? 0.6 : 1,
            }}
          >
            Disable listing
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() => onAction("fix", note || undefined)}
            className="ba-btn ba-btn--ghost ba-btn--sm"
          >
            Mark fixed
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() => onAction("dismiss", note || undefined)}
            className="ba-btn ba-btn--ghost ba-btn--sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {c.status !== "pending" && c.admin_note && (
        <p className="ba-mono ba-mono--dim" style={{ fontSize: 11 }}>
          Admin note: {c.admin_note}
        </p>
      )}
    </article>
  );
}
